import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { isMobile, isDesktop, isIOS } from 'react-device-detect';
import ReactDOM from 'react-dom';
import { useSwipeable } from 'react-swipeable';
import { Controller3D } from '../../../Controller/3DController/3DController';
import { AudioController } from '../../../Controller/3DController/AudioController';
import { ConfiguratorModel } from '../../../Controller/3DController/ConfiguratorModel';
import { DeviceOrientationCameraController } from '../../../Controller/3DController/DeviceOrientationCameraController';
import { RenderEngine } from '../../../Controller/3DController/RenderEngine';
import { SequenceController } from '../../../Controller/3DController/SequenceController';
import { translate } from '../../../Controller/3DController/translateController';
import { Color, Design, Experience, Info, Lights, Share, Wheels, ChevronLeft, ChevronRight, Pattern, Ambilight, Airbag, Exit, DoorsClosed, Seats, Xray, CarSwitching, Driving, Trunk, Dummy, SwipeUpArrow, Environment, Gyro, AR, Highlight, SoundOn, SoundOff, DoorsOpen, LightsOff, On } from '../Icons/Icons';

import '../Menu/Menu.scss';
import './MenuDesktop.scss';

export interface MenuDesktopProps {
    switchingModel: () => void;
    closeMenu: () => void;
    toggleHotSpot: () => void;
    hotSpot: boolean;

    changeMenu: (boolean) => void;
    menuInteraction: boolean;
    menuConfig: boolean;

    engine: RenderEngine;
    modelController: ConfiguratorModel;
    lightOn: boolean;
    sequenceController: SequenceController;

    currentMenu: any;
    turnOnSubMenu: (submenu: string) => boolean;
    checkMenuActive: (submenu: string) => boolean;
    screenshotMode: () => void;
    drive: () => void;

    trunkLoadingMode:()=>void,
    genericPassengerMode:()=>void,
    xRayMode:()=>void,
    modelName:string;
    modelLine:string;
    toggleMenu:()=>void,
    presentation:()=>void,
    appendHotspot:()=>void,
    ARQR:()=>void,
    warning:boolean,
    menuActive:boolean,
    menuActiveAnimation:boolean,
    interior:boolean,
    toogleInterior:()=>void,
    doorOpen:boolean,
    controller3D:Controller3D,
    deviceOrientationCameraController:DeviceOrientationCameraController,
    highlightAR:boolean,
    gyroEnabled:boolean,
    exporting:boolean,
    onEnterAR:()=>void,
    onLeaveAR:()=>void,
    mediaQueryIpad:boolean,
    intensity:number,
    airbag:boolean,
    sharing:boolean,
    toogleSharing:()=>void,
    xrActive:boolean,
    sequenceMode: () => void,
    audioController: AudioController,
}

export const MenuDesktop: React.FC<MenuDesktopProps> = (props) => {
    const [isMenuExpanded, setMenuExpanded] = useState<boolean>(false);
    const [isMenuConfigActive, setMenuConfigActive] = useState<boolean>(false);
    const [isMenuInteractiveActive, setMenuInteractiveActive] = useState<boolean>(false);
    const [isLandscape, setLandscape] = useState<boolean>(false);
    const [showHelp, setShowHelp] = useState<boolean>(true);
    const [shouldDisplayModelName, setDisplayModelName] = useState<boolean>(true);
    const [isStartingAR, setStartingAR] = useState<boolean>(false);
    const [isMuted, setMuted] = useState<boolean>(props.audioController.muted);

    const scrollRef = useRef<HTMLDivElement>(null);
    const activeItemRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        checkOrientation();

        window.addEventListener('resize', checkOrientation);

        return () => {
            window.removeEventListener('resize', checkOrientation);
        }
    }, []);

    useEffect(() => {
        if (shouldDisplayModelName) {
            setTimeout(() => {
                setDisplayModelName(false);
            }, 3500);
        }
    }, [shouldDisplayModelName]);

    useEffect(() => {
        setStartingAR(false);
    }, [props.xrActive]);

    const toggleAudio = useCallback(() => {
        props.audioController.muted = !props.audioController.muted;
        setMuted(props.audioController.muted);
    }, []);

    useEffect(() => {
        let timeout = undefined;

        if (!isMenuExpanded && props.interior) {
            props.toogleInterior();
        }

        if (isMenuExpanded) {
            if (!isMenuInteractiveActive && !isMenuConfigActive) {
                toggleConfigMenu();
                checkActiveSubMenu();
                props.changeMenu(true);
            }
            setShowHelp(true);

            timeout = setTimeout(() => {
                setShowHelp(false);
            }, 5000);
        }

        return () => {
            clearTimeout(timeout);
        };
    }, [isMenuExpanded]);

    useEffect(() => {
        if (props.hotSpot) {
            setShowHelp(true);
        } else {
            setShowHelp(false);
        }
    }, [props.hotSpot]);

    useEffect(() => {
        if (!isMenuConfigActive && props.menuConfig) {
            props.changeMenu(true);
        }

        if (props.checkMenuActive('seat')) {
            props.turnOnSubMenu('Car Paint');
        }
    }, [isMenuConfigActive]);

    useEffect(() => {
        if (!isMenuInteractiveActive && props.menuInteraction) {
            props.changeMenu(false);
        }
    }, [isMenuInteractiveActive]);

    useEffect(() => {
        if (props.menuConfig) {
            if (!isMenuConfigActive) { 
                toggleConfigMenu();
            }

            if (!isMenuExpanded) {
                setMenuExpanded(true);
            }

            checkActiveSubMenu();
        } else {
            if (props.checkMenuActive('Wheels')) {
                props.turnOnSubMenu('Wheels');
            }

            if (isMenuConfigActive) {
                toggleConfigMenu();
            }
        }
    }, [props.menuConfig]);

    useEffect(() => {
        if (props.menuInteraction) {
            if (!isMenuInteractiveActive) {
                toggleInteractiveMenu();
            }

            if (!isMenuExpanded) {
                setMenuExpanded(true);
            }

            checkActiveSubMenu();
        } else {
            if (isMenuInteractiveActive) {
                toggleInteractiveMenu();
            }
        }
    }, [props.menuInteraction]);

    useEffect(() => {
        if (!props.interior && props.checkMenuActive('seat')) {
            if (isMenuConfigActive) {
                props.turnOnSubMenu('Car Paint');
            } else {
                props.turnOnSubMenu(null);
            }
        }

        if (!props.menuConfig) return;

        checkActiveSubMenu();
    }, [props.interior]);

    const toggleConfigMenu = useCallback(() => {
        if (isMenuConfigActive) {
            if (props.menuConfig) {
                props.changeMenu(true);
            }

            setMenuConfigActive(false);
        } else {
            setMenuConfigActive(true);
        }
    }, [isMenuConfigActive]);

    const toggleInteractiveMenu = useCallback(() => {
        if (isMenuInteractiveActive) {
            if (props.menuInteraction) {
                props.changeMenu(false);
            }

            setMenuInteractiveActive(false);
        } else {
            setMenuInteractiveActive(true);
        }
    }, [isMenuInteractiveActive]);

    const checkActiveSubMenu = () => {
        if (props.menuConfig) {
            if (!props.checkMenuActive('Car Paint') && !props.interior) {
                props.turnOnSubMenu('Car Paint');
            } else if (!props.checkMenuActive('interior') && props.interior) {
                props.turnOnSubMenu('interior');
            }
        }
    };

    useLayoutEffect(() => {
        if (scrollRef.current && activeItemRef.current) {
            centerSelectedSubMenuItem();
        }
    });

    useEffect(() => {
        centerSelectedSubMenuItem();
    }, [props.currentMenu]);

    const checkOrientation = () => {
        if (window.matchMedia('(orientation: landscape)').matches) {
            setLandscape(true);
        } else {
            setLandscape(false);
        }
    };

    const renderMenuSelector = () => (
        <div className={`vw-menu-desktop__menu-selector ${isMenuConfigActive || isMenuInteractiveActive ? 'submenu-active' : ''}`}>
            <div
                className="vw-menu-desktop__menu-selector-item"
                onClick={() => {
                    toggleConfigMenu();
                    if (!props.menuConfig) {
                        props.changeMenu(true);
                    }
                }}
            >
                <Design />
                <span className={`section-item-title ${(!showHelp && !props.hotSpot) ? 'hidden' : ''}`}>{translate("id_personalize")}</span>
            </div>

            <div
                className="vw-menu-desktop__menu-selector-item"
                onClick={() => {
                    toggleInteractiveMenu();
                    if (!props.menuInteraction) {
                        props.changeMenu(false);
                    }
                }}
            >
                <Experience />
                <span className={`section-item-title ${(!showHelp && !props.hotSpot) ? 'hidden' : ''}`}>{translate("id_experience")}</span>
            </div>

            <div
                className="vw-menu-desktop__menu-selector-item"
                onClick={props.switchingModel}
            >
                <CarSwitching />
                <span className={`section-item-title ${(!showHelp && !props.hotSpot) ? 'hidden' : ''}`}>{translate("Model Change")}</span>
            </div>

            <div
                className={`vw-menu-desktop__menu-selector-item ${!isMuted ? 'active' : ''} sound-toggler`}
                onClick={toggleAudio}
            >
                {isMuted ? <SoundOff /> : <SoundOn />}
                <span className={`section-item-title ${(!showHelp && !props.hotSpot) ? 'hidden' : ''}`}>{isMuted ? translate("Sound off") : translate("Sound on")}</span>
            </div>
        </div>
    );

    const renderMenuConfig = () => {
        return (
            <>
                {props.interior ? (
                    <>
                        <div
                            className={`section-item ${props.checkMenuActive('interior') ? 'selected' : ``}`}
                            onClick={() => {
                                if (props.checkMenuActive('interior')) return;
                                props.turnOnSubMenu('interior');
                            }}
                        >
                            <Pattern />
                        </div>
                        {!props.xrActive && (<div
                            className={`section-item ${props.checkMenuActive('Environment') ? `selected` : ``}`}
                            onClick={() => {
                                if (props.checkMenuActive('Environment')) return;
                                props.turnOnSubMenu('Environment');
                            }}
                        >
                            <Environment />
                        </div>)}
                        {props.modelController.ambilightColorSelectionGroup && (
                            <div
                                className={`section-item ${props.checkMenuActive('Ambilight Color') ? 'selected' : ``}`}
                                onClick={() => {
                                    if (props.checkMenuActive('Ambilight Color')) return;
                                    props.modelController.setAmbilightIntensity(1, true);
                                    props.turnOnSubMenu('Ambilight Color');
                                }}
                            >
                                <Ambilight />
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        <div
                            className={`section-item ${props.checkMenuActive('Car paint') ? 'selected' : ``}`}
                            onClick={() => {
                                if (props.checkMenuActive('Car Paint')) return;
                                props.turnOnSubMenu('Car paint');
                            }}
                        >
                            <Color />
                        </div>
                        <div
                            className={`section-item ${props.checkMenuActive('Wheels') ? 'selected' : ``}`}
                            onClick={() => {
                                if (props.checkMenuActive('Wheels')) return;
                                props.turnOnSubMenu('Wheels');
                            }}
                        >
                            <Wheels />
                        </div>
                        <div
                            className={`section-item ${props.lightOn ? `active` : ``}`}
                            onClick={() => props.modelController.turnLight(true)}
                        >
                            {props.lightOn ? <Lights /> : <LightsOff />}
                        </div>
                        {!props.xrActive && (<div
                            className={`section-item ${props.checkMenuActive('Environment') ? `selected` : ``}`}
                            onClick={() => {
                                if (props.checkMenuActive('Environment')) return;
                                props.turnOnSubMenu('Environment');
                            }}
                        >
                            <Environment />
                        </div>)}
                        <div
                            className="section-item"
                            onClick={() => props.screenshotMode()}
                        >
                            <Share />
                        </div>
                    </>
                )}
            </>
        );
    };

    const renderMenuInteraction = () => {
        return (
            <>
                {props.interior ? (
                    <>
                        {(!props.modelController.config.features || props.modelController.config.features.airbag) && (
                            <div
                                className={`section-item ${props.airbag ? 'active' : ''}`}
                                onClick={() => props.controller3D.playAirbagsAnimation(true)}
                            >
                                <Airbag />
                            </div>
                        )}
                        <div
                            className={`section-item ${props.doorOpen ? 'active' : ``}`}
                            onClick={() => props.controller3D.playAnimation(true)}
                        >
                            {props.doorOpen ? <DoorsOpen /> : <DoorsClosed />}
                        </div>
                        {!props.xrActive && <div
                            className={`section-item ${props.checkMenuActive('seat') ? 'selected' : ``}`}
                            onClick={() => props.turnOnSubMenu('seat')}
                        >
                            <Seats />
                        </div>}
                    </>
                ) : (
                    <>
                        <div
                            className="section-item"
                            onClick={props.drive}
                        >
                            <Driving />
                        </div>
                        <div
                            className="section-item"
                            onClick={props.trunkLoadingMode}
                        >
                            <Trunk />
                        </div>
                        <div
                            className="section-item"
                            onClick={props.xRayMode}
                        >
                            <Xray />
                        </div>
                        <div
                            className="section-item"
                            onClick={props.genericPassengerMode}
                        >
                            <Dummy />
                        </div>
                        <div
                            className="section-item"
                            onClick={props.sequenceMode}
                        >
                            <Highlight />
                        </div>
                        <div
                            className={`section-item ${props.doorOpen ? 'active' : ``}`}
                            onClick={() => props.controller3D.playAnimation(true)}
                        >
                            {props.doorOpen ? <DoorsOpen /> : <DoorsClosed />}
                        </div>
                    </>
                )}
            </>
        );
    };

    const getSubMenu = (showName = true, scrollable = true) => {
        if (!props.currentMenu) return null;

        if (props.currentMenu.type === 'colorPicker') {
            let color = `linear-gradient(to right`;

            props.modelController.ambilightColorCodes.forEach((ar, i) => {
                let arMap = ar.map(e => (e * 255).toString());
                color += `, rgb(${arMap.join(',')})`;
            });

            color += ')';

            return (
                <div className={`vw-menu-desktop__horizontal-slider ${isLandscape ? 'landscape' : ''}`}>
                    <div className="vw-menu-desktop__horizontal-slider-holder">
                        <div className="vw-menu-desktop__horizontal-slider-wrapper">
                            <input
                                type="range"
                                min="0"
                                style={{ backgroundImage: color }}
                                max={props.currentMenu.selections.length - 1}
                                value={props.currentMenu.currentSelection}
                                onChange={(e) => props.modelController.changeSelectionByIndex(props.currentMenu.groupID, e.target.value)}
                            />
                            <div
                                className="vw-menu-desktop__horizontal-slider-thumb"
                                style={{
                                    left: `${(props.currentMenu.currentSelection/(props.currentMenu.selections.length-1)*100)+"%"}`,
                                    bottom: `${'0'}`,
                                    transform: `${"translateY(-50%) translateX(-" + (props.currentMenu.currentSelection/(props.currentMenu.selections.length-1)*100) + "%)"}`
                                }}>
                                <span className={"thumb-background"}></span>
                                <span></span>
                            </div>
                        </div>
                        <div
                            className={`section-item ${props.intensity === 0 ? `` : `active`}`}
                            onClick={() => {
                                if (props.intensity === 0) {
                                    props.modelController.setAmbilightIntensity(1, true);
                                } else {
                                    props.modelController.setAmbilightIntensity(0, true);
                                }
                            }}
                        >
                            <On />
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <>
                {props.currentMenu.selections.map((selection, i) => {
                    let img = `./${HASH}/options/${selection.id.replace("*", "")}.png`;

                    if (props.currentMenu.type === 'material') {
                        img = props.engine.getMaterialImagePath(selection.id);
                    } else if (props.currentMenu.type === 'seat') {
                        img = `./${HASH}/Icons_Png/${selection.id}.svg`;
                    }

                    return props.modelController.disable.includes(selection.id) ? null : (
                        <div
                            key={selection.id}
                            ref={(props.currentMenu.currentSelection === i) ? activeItemRef : null}
                            className={`section-item ${props.currentMenu.currentSelection === i ? `selected` : ``}`}
                            onClick={(evt) => onSubMenuItemClicked(evt, selection)}
                        >
                            <img className={'section-item-img'} src={img} />
                        </div>
                    );
                })}
            </>           
        );
    }

    const onSubMenuItemClicked = (event, currentItem) => {
        toggleSelectedSubMenuItem(currentItem);
        centerSelectedSubMenuItem();
    };

    const toggleSelectedSubMenuItem = (currentItem) => {
        props.modelController.changeSelection(props.currentMenu.groupID, currentItem.id, true);
    };

    const centerSelectedSubMenuItem = () => {
        const scrollContainer = ReactDOM.findDOMNode(scrollRef.current) as HTMLDivElement;
		const activeItem = ReactDOM.findDOMNode(activeItemRef.current) as HTMLDivElement;

        if (!scrollContainer || !activeItem) return;
        const scrollParent = ReactDOM.findDOMNode(scrollRef.current.parentElement) as HTMLDivElement;

        const scrollParentRect = scrollParent.getBoundingClientRect();
        const activeRect = activeItem.getBoundingClientRect();
        
        let newPosition;

        if (isLandscape) {
            newPosition = scrollParentRect.height - (activeItem.offsetTop + (activeItem.offsetWidth + 20)) - ((scrollParentRect.height / 2) - (activeRect.width));
        } else {
            newPosition = scrollParentRect.width - (activeItem.offsetLeft + (activeItem.offsetWidth + 20)) - ((scrollParentRect.width / 2) - (activeRect.width));
        }

        if (newPosition > 0) {
            if (isLandscape) {
                scrollContainer.style.top = '0px';
                scrollContainer.style.left = undefined;
            } else {
                scrollContainer.style.top = undefined;
                scrollContainer.style.left = '0px';
            }           
        } else {
            if (isLandscape) {
                scrollContainer.style.top = `${newPosition}px`;
                scrollContainer.style.left = undefined;
            } else {
                scrollContainer.style.top = undefined;
                scrollContainer.style.left = `${newPosition}px`;
            }
        }
    };

    const swipeUpHandlers = useSwipeable({
        onSwipedUp: () => {
            if (!isLandscape) setMenuExpanded(true);
        },
        onSwipedLeft: () => {
            if (isLandscape) setMenuExpanded(true);
        },
        preventDefaultTouchmoveEvent: true,
        trackMouse: true,
    });

    const swipeDownHandlers = useSwipeable({
        onSwipedDown: () => {
            if (!isLandscape) setMenuExpanded(false);
        },
        onSwipedRight: () => {
            if (isLandscape) setMenuExpanded(false);
        },
        preventDefaultTouchmoveEvent: true,
        trackMouse: true,
    });

    return (
        <>
            <div className="vw-menu-desktop__main-menu-wrapper">
                <div className="vw-menu-desktop">
                    <div className={`vw-menu-desktop__main ${isMenuExpanded ? 'active' : ''}`}>
                        {(props.interior && isMobile) ? (
                            <div
                                className={`vw-menu-desktop__main-item ${(props.gyroEnabled) ? 'active' : ''}`}
                                onClick={() => props.deviceOrientationCameraController.toggle()}
                            >
                                <Gyro />
                            </div>
                        ) : (
                            <div
                                className="vw-menu-desktop__main-item"
                                onClick={() =>  {
                                    if (history.length <= 1) {
                                        window.location.href = 'https://www.vw.com.mx/es.html';
                                    } else {
                                        history.back();
                                    }
                                }}
                            >
                                <Exit />
                            </div>
                        )}
                        
                        <div
                            className={`vw-menu-desktop__main-item ${props.hotSpot ? `active` : ``}`}
                            onClick={props.toggleHotSpot}
                        >
                            <Info />
                        </div>
                    </div>

                    <div
                        {...swipeUpHandlers}
                        className={`vw-menu-desktop__swipe-up ${isMenuExpanded ? 'hidden' : ''}`}
                        onClick={() => setMenuExpanded(true)}
                    >
                        <span className={!shouldDisplayModelName ? `hidden` : ''}>
                            <strong>{translate("Click to start")}</strong><br />
                            {translate("and explore your VW")}
                        </span>

                        <div className="vw-menu-desktop__swipe-up-button">
                            <div className="vw-menu-desktop__swipe-up-button_arrows">
                                <div className="vw-menu-desktop__swipe-up-button_arrows-1">
                                    <SwipeUpArrow />
                                </div>
                                <div className="vw-menu-desktop__swipe-up-button_arrows-2">
                                    <SwipeUpArrow />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* <div
                        {...swipeDownHandlers}
                        className={`vw-menu-desktop__close ${(!isMenuExpanded || isMenuConfigActive || isMenuInteractiveActive) ? 'hidden' : ''}`}
                        onClick={() => setMenuExpanded(false)}
                    >
                        <SwipeUpArrow />
                    </div> */}

                    <div className={`vw-menu-desktop__main-menu ${isMenuExpanded ? 'active' : ''}`}>
                        <div className="vw-menu-desktop__menu-wrapper">
                            <div className="vw-menu-desktop__main-menu-section">
                                <div className="vw-menu-desktop__main-menu-close">
                                    <div
                                        className="button AR"
                                        onClick={() => {
                                            if (isDesktop && !isIOS) {
                                                props.ARQR();
                                            } else {
                                                if (!props.xrActive) props.onEnterAR();
                                            }
                                        }}
                                    >
                                        <AR /> {translate('id_ar')}
                                    </div>
                                    <div className="button" onClick={() => setMenuExpanded(false)}>
                                        <ChevronRight />
                                    </div>
                                </div>

                                {/* AR/3D Toggler */}
                                {/* {!isMenuInteractiveActive && !isMenuConfigActive && !props.interior && (
                                    <div className={`vw-menu-desktop__view-toggler small ${props.xrActive ? 'active' : ''}`}>
                                        <div
                                            className={`item ${!props.xrActive ? 'active' : ''}`}
                                            onClick={() => {
                                                if (props.xrActive) props.onLeaveAR();
                                            }}
                                        >
                                            3D
                                        </div>
                                        <div
                                            className={`item ${props.xrActive ? 'active' : ''}`}
                                            onClick={() => {
                                                if (isDesktop && !isIOS) {
                                                    props.ARQR();
                                                } else {
                                                    if (!props.xrActive) props.onEnterAR();
                                                }
                                            }}
                                        >
                                            {translate('id_ar')}
                                        </div>
                                    </div>
                                )} */}

                                {!props.xrActive && (
                                    <div className={`vw-menu-desktop__view-toggler ${props.interior ? 'active' : ''}`}>
                                        <div
                                            className={`item ${!props.interior ? 'active' : ''}`}
                                            onClick={() => {
                                                if (props.interior) props.toogleInterior();
                                            }}
                                        >
                                            Exterior
                                        </div>
                                        <div
                                            className={`item ${props.interior ? 'active' : ''}`}
                                            onClick={() => {
                                                if (!props.interior) props.toogleInterior();
                                            }}
                                        >
                                            Interior
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="vw-menu-desktop__main-menu-section two-column">
                                <div className="vw-menu-desktop__main-menu__column sub">
                                    {(isMenuConfigActive) && renderMenuConfig()}
                                    {(isMenuInteractiveActive) && renderMenuInteraction()}
                                </div>
                                <div className="vw-menu-desktop__main-menu__column main">
                                    <div
                                        className={`section-item ${isMenuConfigActive ? 'selected' : ''}`}
                                        onClick={() => {
                                            if (!isMenuConfigActive) {
                                                toggleConfigMenu();
                                                if (isMenuInteractiveActive) setMenuInteractiveActive(false);
                                                if (!props.menuConfig) {
                                                    props.changeMenu(true);
                                                }
                                            }
                                        }}
                                    >
                                        <Design />
                                        <span className={`section-item-title ${(!showHelp && !props.hotSpot) ? 'hidden' : ''}`}>{translate("id_personalize")}</span>
                                    </div>

                                    <div
                                        className={`section-item ${isMenuInteractiveActive ? 'selected' : ''}`}
                                        onClick={() => {
                                            if (!isMenuInteractiveActive) {
                                                toggleInteractiveMenu();
                                                if (isMenuConfigActive) setMenuConfigActive(false);
                                                if (!props.menuInteraction) {
                                                    props.changeMenu(false);
                                                }
                                            }
                                        }}
                                    >
                                        <Experience />
                                        <span className={`section-item-title ${(!showHelp && !props.hotSpot) ? 'hidden' : ''}`}>{translate("id_experience")}</span>
                                    </div>

                                    <div
                                        className="section-item"
                                        onClick={props.switchingModel}
                                    >
                                        <CarSwitching />
                                        <span className={`section-item-title ${(!showHelp && !props.hotSpot) ? 'hidden' : ''}`}>{translate("Model Change")}</span>
                                    </div>

                                    <div
                                        className={`section-item ${!isMuted ? 'active' : ''} sound-toggler`}
                                        onClick={toggleAudio}
                                    >
                                        {isMuted ? <SoundOff /> : <SoundOn />}
                                        <span className={`section-item-title ${(!showHelp && !props.hotSpot) ? 'hidden' : ''}`}>{isMuted ? translate("Sound off") : translate("Sound on")}</span>
                                    </div>
                                </div>
                            </div>

                            {(isMenuConfigActive || isMenuInteractiveActive) && (<div className={`vw-menu-desktop__main-menu-section ${(props.currentMenu && props.currentMenu.type === 'colorPicker') ? '' : 'menu-submenu'}`}>
                                {getSubMenu()}
                            </div>)}

                            {(isMenuInteractiveActive || isMenuConfigActive)
                                && props.currentMenu
                                && props.currentMenu.type != 'colorPicker'
                                && props.currentMenu.selections[props.currentMenu.currentSelection].name
                                && (
                                    <div className="vw-menu-desktop__main-menu-section item-name">
                                        {translate(props.currentMenu.selections[props.currentMenu.currentSelection].name)}
                                    </div>
                                )
                            }
                        </div>

                        {/* {renderMenuSelector()}

                        {renderMenuConfig()}

                        {renderMenuInteraction()} */}
                    </div>
                </div>

                {/* {isLandscape && (
                    <div className={`vw-menu-desktop__left-menu ${(isMenuConfigActive || isMenuInteractiveActive) ? 'active' : ''}`}>
                        {(isMenuConfigActive || isMenuInteractiveActive) && getSubMenu(false, true)}
                    </div>
                )} */}

                {/* Model Name */}
                {!props.xrActive && (
                    <div className={`vw-menu-desktop__model-name ${(!shouldDisplayModelName || isMenuExpanded) && isMobile ? 'hidden' : ''}`}>
                        <div className="vw-menu-desktop__model-name-new">{props.modelName.toLowerCase().indexOf('nuevo') === 0 ? 'Nuevo' : '\u00A0'}</div>
                        {props.modelName.toLowerCase().indexOf('nuevo') === 0
                            ? <div className="vw-menu-desktop__model-name-model">{props.modelName.replace('Nuevo ', '')}</div>
                            : <div className="vw-menu-desktop__model-name-model">{props.modelName}</div>
                        }
                        {props.modelLine && (
                            <div className="vw-menu-desktop__model-name-variant">{props.modelLine}</div>
                        )}
                    </div>
                )}

                {/* {(isMenuInteractiveActive || (isMenuConfigActive && isLandscape)) && props.currentMenu && props.currentMenu.type != 'colorPicker' && props.currentMenu.selections[props.currentMenu.currentSelection].name && (
                    <div className="vw-menu-desktop__current-menu-name">{translate(props.currentMenu.selections[props.currentMenu.currentSelection].name)}</div>
                )} */}

                {isIOS && props.exporting && (
                    <div className="vw-menu-desktop__loading">
                        <div className="vw-menu-desktop__loading-icon">
                            <AR />
                        </div>
                        <div className="vw-menu-desktop__loading-text">{translate('Preparing AR Experience...')}</div>
                    </div>
                )}
            </div>
        </>
    );
};

export default MenuDesktop;